import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  ArrowLeft,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  ChevronLeft,
  ChevronRight,
  Download,
  Save,
  Pencil,
  Printer,
} from 'lucide-react';
import { isDesktop, saveFile } from '@/lib/fileSystem';
import SourceChip from './SourceChip';

/**
 * Toolbar — Top bar for the PDF viewer.
 * Zoom controls, page navigation, fit-to-width toggle, download, and back button.
 */
export default function Toolbar({
  currentPage,
  numPages,
  zoom,
  isFitted,
  onZoomIn,
  onZoomOut,
  onFitToWidth,
  onResetZoom,
  onPrevPage,
  onNextPage,
  onGoToPage,
  fileName,
  onFileNameChange,
  file,
  showAnnotate,
  annotateActive,
  onToggleAnnotate,
  editActive,
  onToggleEdit,
  hasUnsavedChanges,
  onBack,
  onSetZoom,
  onSave,
  hideSave = false,
  onPrint,
  onDownload,              // web download icon: always local download, never cloud-save
  // Cloud-aware state (new props; all optional so callers can migrate lazily)
  docSource,               // 'cloud' | 'device' | 'new'
  cloudSyncStatus,         // 'synced' | 'saving' | 'error'
  cloudAvailable,          // whether this user can move a local doc to cloud
  onMoveToCloud,           // device → cloud handler
  onViewCloud,             // sync error → cloud page
}) {
  const navigate = useNavigate();

  // Editable filename
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const nameInputRef = useRef(null);

  const startEditName = () => {
    setNameValue(fileName || 'document.pdf');
    setEditingName(true);
  };

  useEffect(() => {
    if (editingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingName]);

  const commitName = () => {
    setEditingName(false);
    if (nameValue.trim() && onFileNameChange) {
      onFileNameChange(nameValue.trim());
    }
  };

  // Editable zoom
  const [editingZoom, setEditingZoom] = useState(false);
  const [zoomValue, setZoomValue] = useState('');
  const zoomInputRef = useRef(null);

  const startEditZoom = () => {
    setZoomValue(String(Math.round(zoom * 100)));
    setEditingZoom(true);
  };

  useEffect(() => {
    if (editingZoom && zoomInputRef.current) {
      zoomInputRef.current.focus();
      zoomInputRef.current.select();
    }
  }, [editingZoom]);

  const commitZoom = () => {
    setEditingZoom(false);
    const val = parseInt(zoomValue, 10);
    if (!isNaN(val) && val >= 25 && val <= 500 && onSetZoom) {
      onSetZoom(val / 100);
    }
  };

  const handleSaveClick = useCallback(async () => {
    // The toolbar icon next to the filename switches by platform:
    //   desktop → Save icon (in-place save / dialog)
    //   web     → Download icon (local download)
    // Pick the matching handler so a web user clicking the download arrow on
    // a cloud-sourced doc gets an actual local download, not a cloud-save.
    const handler = onSave || onDownload;
    if (handler) { handler(); return; }
    // Fallback: download the raw original file (no edits)
    if (!file) return;
    if (isDesktop()) {
      const buffer = await file.arrayBuffer();
      await saveFile(new Uint8Array(buffer), fileName || 'document.pdf');
    } else {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document.pdf';
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [file, fileName, onSave, onDownload]);

  // Derive the chip's source/status from the explicit props or fall back to
  // the existing "dirty state" signaling so the Toolbar renders sensibly on
  // pages that haven't migrated yet.
  const chipSource = docSource === 'cloud'
    ? 'cloud'
    : (hasUnsavedChanges && !editActive) ? 'unsaved' : 'device';
  const chipStatus = cloudSyncStatus || (hasUnsavedChanges ? 'saving' : 'synced');
  const showSourceChip = Boolean(file);

  return (
    <div className="flex items-center justify-between h-12 px-4 bg-white border-b border-gray-200 shrink-0">
      {/* Left: Back + source chip + filename */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onBack || (() => navigate('/'))}
          className="p-1.5 rounded-md hover:bg-brand-light transition-colors text-gray-500 hover:text-brand-blue"
          title="Back to Dashboard"
          aria-label="Back to Dashboard"
        >
          <ArrowLeft size={18} />
        </button>
        {showSourceChip && (
          <SourceChip
            source={chipSource}
            status={chipStatus}
            cloudAvailable={Boolean(cloudAvailable && chipSource === 'device')}
            onMoveToCloud={onMoveToCloud}
            onViewCloud={onViewCloud}
          />
        )}
        {editingName ? (
          <input
            ref={nameInputRef}
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              if (e.key === 'Escape') setEditingName(false);
            }}
            className="text-sm font-medium text-gray-700 max-w-[200px] px-1 border border-brand-blue rounded outline-none"
          />
        ) : (
          <span
            onClick={startEditName}
            className="text-sm font-medium text-gray-700 truncate max-w-[200px] cursor-pointer hover:text-brand-blue"
            title="Click to rename"
          >
            {fileName || 'PDF Viewer'}
          </span>
        )}
        {file && !hideSave && (
          <button
            onClick={handleSaveClick}
            className="p-1.5 rounded-md hover:bg-brand-light transition-colors text-gray-400 hover:text-brand-blue"
            title={isDesktop() ? `Save ${fileName || 'PDF'}` : `Download ${fileName || 'PDF'}`}
            aria-label={isDesktop() ? 'Save PDF' : 'Download PDF'}
          >
            {isDesktop() ? <Save size={16} /> : <Download size={16} />}
          </button>
        )}
        {file && onPrint && <button onClick={onPrint} aria-label="Print document" title="Print document" className="p-1.5 rounded-md hover:bg-brand-light text-gray-400 hover:text-brand-blue"><Printer size={16} /></button>}
      </div>

      {/* Center: Page navigation */}
      <div className="flex items-center gap-1">
        <button
          onClick={onPrevPage}
          disabled={currentPage <= 1}
          className="p-1.5 rounded-md hover:bg-brand-light transition-colors text-gray-500 hover:text-brand-blue disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous page"
          aria-label="Previous page"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-1.5 text-sm">
          <input
            type="number"
            min={1}
            max={numPages}
            value={currentPage}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val)) onGoToPage(val);
            }}
            className={`h-7 text-center border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue/20 ${
              numPages > 9999 ? 'w-16' : numPages > 999 ? 'w-14' : 'w-12'
            }`}
            aria-label={`Page ${currentPage} of ${numPages}`}
          />
          <span className="text-gray-400">/</span>
          <span className="text-gray-500">{numPages}</span>
        </div>
        <button
          onClick={onNextPage}
          disabled={currentPage >= numPages}
          className="p-1.5 rounded-md hover:bg-brand-light transition-colors text-gray-500 hover:text-brand-blue disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next page"
          aria-label="Next page"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Right: Edit + Zoom controls */}
      <div className="flex items-center gap-1">
        {showAnnotate && (
          <>
            <button
              onClick={onToggleEdit}
              className={`relative flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium transition-colors ${
                editActive
                  ? 'text-white'
                  : 'text-gray-500 hover:bg-brand-light hover:text-brand-blue'
              }`}
              style={editActive ? { background: 'linear-gradient(135deg, #4F46E5, #6366F1)' } : {}}
              title="Edit mode"
            >
              <Pencil size={15} />
              <span className="hidden sm:inline text-xs">Edit</span>
              {hasUnsavedChanges && !editActive && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-accent-coral rounded-full" />
              )}
            </button>
            <div className="w-px h-5 bg-gray-200 mr-1" />
          </>
        )}
        <button
          onClick={onZoomOut}
          className="p-1.5 rounded-md hover:bg-brand-light transition-colors text-gray-500 hover:text-brand-blue"
          title="Zoom out"
          aria-label="Zoom out"
        >
          <ZoomOut size={18} />
        </button>
        {editingZoom ? (
          <input
            ref={zoomInputRef}
            value={zoomValue}
            onChange={(e) => setZoomValue(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={commitZoom}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitZoom();
              if (e.key === 'Escape') setEditingZoom(false);
            }}
            className="h-6 w-14 text-center text-sm text-gray-500 border border-brand-blue rounded outline-none"
          />
        ) : (
          <span
            onClick={startEditZoom}
            className="text-sm text-gray-500 w-14 text-center tabular-nums cursor-pointer hover:text-brand-blue"
            title="Click to set zoom"
          >
            {Math.round(zoom * 100)}%
          </span>
        )}
        <button
          onClick={onZoomIn}
          className="p-1.5 rounded-md hover:bg-brand-light transition-colors text-gray-500 hover:text-brand-blue"
          title="Zoom in"
          aria-label="Zoom in"
        >
          <ZoomIn size={18} />
        </button>
        <button
          onClick={isFitted ? onResetZoom : onFitToWidth}
          className={`p-1.5 rounded-md transition-colors ml-1 ${
            isFitted
              ? 'text-brand-blue bg-brand-light'
              : 'text-gray-500 hover:bg-brand-light hover:text-brand-blue'
          }`}
          title={isFitted ? 'Reset zoom (100%)' : 'Fit to width'}
          aria-label={isFitted ? 'Reset zoom to 100%' : 'Fit to width'}
        >
          {isFitted ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </div>
    </div>
  );
}
